import { useEffect, useState } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { MeshGradient } from "../components/AnimatedBackground";
import { ArrowLeft, Mail, CheckCircle, XCircle, Loader2 } from "lucide-react";

const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://api.nexatice.dev"
    : "http://localhost:3001";

type UnsubscribeStatus = "loading" | "success" | "already" | "error" | "invalid";

export function Unsubscribe() {
  const [status, setStatus] = useState<UnsubscribeStatus>("loading");
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      setStatus("invalid");
      setError("No unsubscribe token provided.");
      return;
    }

    // Call the unsubscribe API
    fetch(`${API_URL}/api/notify/unsubscribe/${token}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setEmail(data.email || "");
          if (data.message?.includes("already")) {
            setStatus("already");
          } else {
            setStatus("success");
          }
        } else {
          setStatus("error");
          setError(data.message || "Failed to unsubscribe.");
        }
      })
      .catch((err) => {
        console.error("Unsubscribe error:", err);
        setStatus("error");
        setError("Something went wrong. Please try again later.");
      });
  }, []);

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="legal" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-xl mx-auto">
          {/* Back link */}
          <a
            href="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>

          {/* Card */}
          <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-2xl p-8 text-center">
            {status === "loading" && (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-zinc-800 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Processing...
                </h1>
                <p className="text-zinc-400">
                  Please wait while we process your request.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  You've been unsubscribed
                </h1>
                <p className="text-zinc-400 mb-6">
                  {email ? (
                    <>
                      <span className="text-white">{email}</span> has been
                      removed from our mailing list.
                    </>
                  ) : (
                    "You've been removed from our mailing list."
                  )}
                </p>
                <p className="text-zinc-500 text-sm">
                  We're sorry to see you go! If you ever change your mind, you
                  can sign up again on our homepage.
                </p>
              </>
            )}

            {status === "already" && (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-zinc-800 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-zinc-500" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Already unsubscribed
                </h1>
                <p className="text-zinc-400 mb-6">
                  {email ? (
                    <>
                      <span className="text-white">{email}</span> is already
                      unsubscribed from our mailing list.
                    </>
                  ) : (
                    "This email is already unsubscribed."
                  )}
                </p>
                <p className="text-zinc-500 text-sm">
                  No further action is needed.
                </p>
              </>
            )}

            {(status === "error" || status === "invalid") && (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {status === "invalid" ? "Invalid Link" : "Something went wrong"}
                </h1>
                <p className="text-zinc-400 mb-6">{error}</p>
                <p className="text-zinc-500 text-sm">
                  If you believe this is an error, please contact us at{" "}
                  <a
                    href="mailto:support@nexatice.dev"
                    className="text-emerald-500 hover:underline"
                  >
                    support@nexatice.dev
                  </a>
                </p>
              </>
            )}

            {/* Action button */}
            <div className="mt-8">
              <a
                href="/"
                className="inline-flex items-center justify-center px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg font-medium transition-colors"
              >
                Go to Homepage
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

